const fontFamily = {
  regular:
    '"GT Ultra Standard Regular", "GT Ultra Standard Regular Fallback", "Times New Roman", serif',
  regularBold:
    '"GT Ultra Standard Bold", "GT Ultra Standard Bold Fallback", "Times New Roman", serif',
  headingBold:
    '"GT Ultra Median Bold", "GT Ultra Median Bold Fallback", "Times New Roman", serif',
};

export const fontWeight = {
  regular: 400,
  bold: 700,
};

export interface ThemeType {
  fontFamily?: string;
  fontWeight?: number;
  fontSize?: string;
  lineHeight?: string;
  textTransform?: string;
  letterSpacing?: string;
}

// Clamp is used to create a fluid typography scale.
// We use this tool to convert https://clamp.font-size.app/
// Maximum viewport width is 1200px
// Minimum viewport width is 376px
// Set mobile/Desktop font size in the calculator
// Convert rem to px tool https://nekocalc.com/px-to-rem-converter

const headingBase = {
  fontWeight: fontWeight.bold,
  fontFamily: fontFamily.headingBold,
};

const bodyLargeBase = {
  fontSize: 'clamp(1.25rem, 1.1359rem + 0.4854vw, 1.5rem)', // 20px => 24px
  lineHeight: 'clamp(1.75rem, 1.6359rem + 0.4854vw, 2rem)', // 28px => 32px
};

const bodyMediumBase = {
  fontSize: '1rem', // 16px
  lineHeight: '1.5rem', // 24px
};

const bodySmallBase = {
  fontSize: '0.875rem', // 14px
  lineHeight: '1.125rem', // 18px
};

const capsBase = {
  fontWeight: fontWeight.bold,
  fontFamily: fontFamily.regularBold,
  textTransform: 'uppercase',
  fontSize: 'clamp(0.625rem, 0.568rem + 0.2427vw, 0.75rem)', // 10px => 12px
  lineHeight: 'clamp(0.625rem, 0.568rem + 0.2427vw, 0.75rem)', // 10px => 12px
};

const underlineBase = {
  textDecoration: 'underline',
};

const typography = {
  headingXLarge: {
    fontSize: 'clamp(2.5rem, 2.1007rem + 1.699vw, 3.375rem);', // 40px => 54px
    lineHeight: 'clamp(3.375rem, 3.1468rem + 0.9709vw, 3.875rem);', // 54px => 62px
    ...headingBase,
  },
  headingLarge: {
    fontWeight: fontWeight.bold,
    fontFamily: fontFamily.headingBold,
    fontSize: 'clamp(2rem, 1.5437rem + 1.9417vw, 3rem);', // 32px => 48px
    lineHeight: 'clamp(2.5rem, 2.0437rem + 1.9417vw, 3.5rem);', // 40px => 56px
  },
  headingMedium: {
    fontWeight: fontWeight.bold,
    fontFamily: fontFamily.headingBold,
    fontSize: 'clamp(1.5rem, 1.2718rem + 0.9709vw, 2rem);', // 24px => 32px
    lineHeight: 'clamp(1.875rem, 1.7039rem + 0.7282vw, 2.25rem);', // 30px => 36px
  },
  headingSmall: {
    fontWeight: fontWeight.bold,
    fontFamily: fontFamily.headingBold,
    fontSize: 'clamp(1.25rem, 1.1359rem + 0.4854vw, 1.5rem)', // 20px => 24px
    lineHeight: 'clamp(1.625rem, 1.4539rem + 0.7282vw, 2rem)', // 26px => 32px
  },
  bodyLarge: {
    fontWeight: fontWeight.regular,
    fontFamily: fontFamily.regular,
    ...bodyLargeBase,
  },
  bodyLargeBold: {
    fontWeight: fontWeight.bold,
    fontFamily: fontFamily.regular,
    ...bodyLargeBase,
  },
  bodyLargeUnderline: {
    fontWeight: fontWeight.regular,
    fontFamily: fontFamily.regular,
    ...bodyLargeBase,
    ...underlineBase,
  },
  bodyheadingBold: {
    fontWeight: fontWeight.bold,
    fontFamily: fontFamily.regularBold,
    ...bodyLargeBase,
  },
  bodyheadingBoldUnderline: {
    fontWeight: fontWeight.bold,
    fontFamily: fontFamily.regularBold,
    ...bodyLargeBase,
    ...underlineBase,
  },
  bodyMedium: {
    fontFamily: fontFamily.regular,
    fontWeight: fontWeight.regular,
    ...bodyMediumBase,
  },
  bodyMediumUnderline: {
    fontFamily: fontFamily.regular,
    fontWeight: fontWeight.regular,
    ...bodyMediumBase,
    ...underlineBase,
  },
  bodyMediumBold: {
    fontFamily: fontFamily.regularBold,
    fontWeight: fontWeight.bold,
    ...bodyMediumBase,
  },
  bodyMediumToSmall: {
    fontFamily: fontFamily.regular,
    fontWeight: fontWeight.regular,
    fontSize: 'clamp(0.875rem, 0.818rem + 0.2427vw, 1rem)', // 14px => 16px
    lineHeight: 'clamp(1.125rem, 0.9539rem + 0.7282vw, 1.5rem)', // 18px => 24px
  },
  bodyMediumToSmallBold: {
    fontFamily: fontFamily.regularBold,
    fontWeight: fontWeight.bold,
    fontSize: 'clamp(0.875rem, 0.818rem + 0.2427vw, 1rem)', // 14px => 16px
    lineHeight: 'clamp(1.125rem, 0.9539rem + 0.7282vw, 1.5rem)', // 18px => 24px
  },
  bodyMediumBoldUnderline: {
    fontWeight: fontWeight.bold,
    ...bodyMediumBase,
    ...underlineBase,
  },
  bodySmall: {
    fontFamily: fontFamily.regular,
    fontWeight: fontWeight.regular,
    ...bodySmallBase,
  },
  bodySmallUnderline: {
    fontFamily: fontFamily.regular,
    fontWeight: fontWeight.regular,
    ...bodySmallBase,
    ...underlineBase,
  },
  bodySmallBold: {
    fontFamily: fontFamily.regularBold,
    fontWeight: fontWeight.bold,
    ...bodySmallBase,
  },
  bodySmallBoldUnderline: {
    fontFamily: fontFamily.regularBold,
    fontWeight: fontWeight.bold,
    ...bodySmallBase,
    ...underlineBase,
  },
  caps: {
    ...capsBase,
  },
  capsUnderline: {
    ...capsBase,
    ...underlineBase,
  },
  quote: {
    fontWeight: fontWeight.regular,
    fontFamily: fontFamily.regular,
    fontSize: 'clamp(1.5rem, 1.2718rem + 0.9709vw, 2rem);', // 24px => 32px
    lineHeight: 'clamp(2rem, 1.7718rem + 0.9709vw, 2.5rem);', // 32px => 40px
  },
} as const;

export default typography;

export type ThemeTypographyKeys = keyof typeof typography;
export type ThemeTypography = Record<ThemeTypographyKeys, ThemeType>;
